// web-frontend/src/shared/constants/config.js - 웹용 (ngrok/https 자동 대응 버전)

import logger from '../utils/logger';

/**
 * 🔥 브라우저 환경 감지
 */
const isMobile = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const isAndroid = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
};

/**
 * 🔥 API Configuration (ngrok/https 자동 대응 버전)
 */
const getApiBaseUrl = () => {
  // 1. 브라우저 환경인지 확인
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const origin = window.location.origin; // 예: https://xxxx.ngrok-free.app

    // 🔥 [핵심 수정] ngrok 또는 배포 환경(Nginx)인 경우
    // Nginx가 /api 경로를 백엔드로 프록시해주므로, 현재 접속한 도메인(https)을 그대로 사용합니다.
    // 이렇게 하면 무조건 현재 브라우저의 프로토콜(https)을 따르므로 Mixed Content가 발생하지 않습니다.
    if (host.includes('ngrok') || host.includes('netzero-sapiens.com') || !host.includes('localhost')) {
      logger.debug('🔒 [Security] Using current origin for API:', origin);
      return origin; 
    }
  }

  // 2. 로컬 개발 환경 (localhost)
  // .env에 VITE_API_BASE_URL이 있다면 사용, 없으면 localhost:8000
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '');
  }

  return 'http://localhost:8000';
};

export const API_CONFIG = {
  BASE_URL: getApiBaseUrl(), // 예: http://localhost:8000 또는 https://xxxx.ngrok-free.app
  API_VERSION: '/api',
  TIMEOUT: 30000, // 30초
  RETRY_ATTEMPTS: 3,
  
  // 전체 API URL (API 요청용)
  get FULL_URL() {
    // BASE_URL이 현재 도메인(origin)이라면 /api를 붙여서 완성
    return `${this.BASE_URL}${this.API_VERSION}`;
  },
  
  ENDPOINTS: {
    // ========================================================================
    // 🔥 인증 (Authentication)
    // ========================================================================
    AUTH: {
      // 기본 인증
      REGISTER: '/auth/register',
      LOGIN: '/auth/login',
      LOGOUT: '/auth/logout',
      ME: '/auth/me',
      REFRESH: '/auth/refresh',
      VERIFY: '/auth/verify',
      RESET_PASSWORD: '/auth/reset-password',
      
      // 🔥 게스트 관련
      GUEST_LOGIN: '/auth/guest-login',
      CONVERT_GUEST: '/auth/convert-guest',
      GUEST_STATUS: '/auth/guest-status',
      
      // 소셜 로그인 엔드포인트
      SOCIAL: {
        GOOGLE: '/auth/google/access-token',
        APPLE: '/auth/apple/token',
        KAKAO: '/auth/kakao/callback',
        NAVER: '/auth/naver/callback',
      },
    },
    
    // ========================================================================
    // 기업 (Companies)
    // ========================================================================
    COMPANIES: {
      LIST: '/companies',
      DETAIL: (id) => `/companies/${id}`,
      CREATE: '/companies',
      UPDATE: (id) => `/companies/${id}`,
      DELETE: (id) => `/companies/${id}`,
      RECOMMENDED: '/companies/recommended',
      SEARCH: '/companies/search',
      STATS: '/companies/stats',
    },
    
    // ========================================================================
    // 미팅 (Meetings)
    // ========================================================================
    MEETINGS: {
      LIST: '/meetings',
      DETAIL: (id) => `/meetings/${id}`,
      CREATE: '/meetings',
      UPDATE: (id) => `/meetings/${id}`,
      DELETE: (id) => `/meetings/${id}`,
      CANCEL: (id) => `/meetings/${id}/cancel`,
      UPCOMING: '/meetings/upcoming',
    },
    
    // ========================================================================
    // 채팅 (Chats)
    // ========================================================================
    CHATS: {
      LIST: '/chats',
      DETAIL: (id) => `/chats/${id}`,
      MESSAGES: (id) => `/chats/${id}/messages`,
      SEND: (id) => `/chats/${id}/send`,
      CREATE: '/chats',
    },
    
    // ========================================================================
    // 기부 (Donations)
    // ========================================================================
    DONATIONS: {
      LIST: '/donations',
      CREATE: '/donations',
      DETAIL: (id) => `/donations/${id}`,
      CAMPAIGNS: '/donations/campaigns',
    },
    
    // ========================================================================
    // 탄소 추적 (Carbon Tracking)
    // ========================================================================
    CARBON: {
      TRACKING: '/carbon/tracking',
      FOOTPRINTS: '/carbon/footprints',
      STATISTICS: '/carbon/stats',
      STATS: '/carbon/stats',
      REPORT: '/carbon/report',
      LEADERBOARD: '/carbon/leaderboard',
    },
    
    // ========================================================================
    // 알림 (Notifications)
    // ========================================================================
    NOTIFICATIONS: {
      LIST: '/notifications',
      MARK_READ: (id) => `/notifications/${id}/read`,
      SETTINGS: '/notifications/settings',
    },
    
    // ========================================================================
    // 통계 (Statistics)
    // ========================================================================
    STATS: {
      DASHBOARD: '/stats/dashboard',
      USER: '/stats/user',
      GLOBAL: '/stats/global',
    },
    
    // ========================================================================
    // 이벤트 (Events)
    // ========================================================================
    EVENTS: {
      LIST: '/events',
      DETAIL: (id) => `/events/${id}`,
      FEATURED: '/events/featured',
    },
  },
};

// ============================================================================
// 🔥 OAuth Configuration - 웹 환경에서는 비활성화 가능
// ============================================================================
export const OAUTH_CONFIG = {
  ENABLED: false, // 웹에서는 기본적으로 비활성화
  
  GOOGLE: {
    ENABLED: false,
    CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
    SCOPES: ['openid', 'profile', 'email'],
  },
  
  // Apple 로그인은 iOS 전용
  APPLE: {
    ENABLED: false,
    CLIENT_ID: '',
  },
  
  KAKAO: {
    ENABLED: false,
    APP_KEY: import.meta.env.VITE_KAKAO_APP_KEY || '',
  },
  
  NAVER: {
    ENABLED: false,
    CLIENT_ID: import.meta.env.VITE_NAVER_CLIENT_ID || '',
    CLIENT_SECRET: import.meta.env.VITE_NAVER_CLIENT_SECRET || '',
    CLIENT_NAME: 'NET ZERO SAPIENS',
  },
};

// ============================================================================
// App Configuration
// ============================================================================
export const APP_CONFIG = {
  NAME: 'NET ZERO SAPIENS',
  VERSION: '1.0.0',
  BUILD_VERSION: 2,
  SCHEME: 'netzero-sapiens',
  
  STORAGE_KEYS: {
    USER_TOKEN: 'user_token',
    USER_DATA: 'user_data',
    LANGUAGE: 'selected_language',
    THEME: 'selected_theme',
    ONBOARDING_COMPLETED: 'onboarding_completed',
    NOTIFICATIONS_ENABLED: 'notifications_enabled',
    COMPANY_BOOKMARKS: 'company_bookmarks',
    IS_GUEST: 'is_guest',
    GUEST_EXPIRATION: 'guest_expiration',
    GUEST_INFO_SEEN: 'guest_info_seen',
    DEVICE_ID: 'device_id',
  },
  
  DEFAULTS: {
    LANGUAGE: 'en',
    THEME: 'light',
    CURRENCY: 'KRW',
    TIMEZONE: 'Asia/Hong_Kong',
  },
  
  FEATURES: {
    BIOMETRIC_AUTH: false, // 웹에서는 비활성화
    PUSH_NOTIFICATIONS: false, // 웹에서는 비활성화
    CARBON_TRACKING: true,
    AI_CURATOR: true,
    DONATIONS: true,
    CHAT_FEATURE: true,
    OAUTH_LOGIN: OAUTH_CONFIG.ENABLED,
    GUEST_LOGIN: true,
  },
  
  LIMITS: {
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
    MAX_UPLOAD_FILES: 5,
    MIN_PASSWORD_LENGTH: 8,
    MAX_MESSAGE_LENGTH: 1000,
    SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24시간
    CACHE_TIMEOUT: 5 * 60 * 1000, // 5분
    GUEST_SESSION_DAYS: 90,
  },
  
  PAGINATION: {
    DEFAULT_PAGE_SIZE: 20,
    COMPANIES_PAGE_SIZE: 10,
    MEETINGS_PAGE_SIZE: 15,
    NOTIFICATIONS_PAGE_SIZE: 20,
    MAX_PAGE_SIZE: 100,
  },
  
  // 🔥 게스트 사용자 권한 제한
  GUEST_RESTRICTIONS: {
    CAN_VIEW_COMPANIES: true,
    CAN_VIEW_EVENTS: true,
    CAN_REQUEST_MEETING: false,
    CAN_USE_CHAT: false,
    CAN_EARN_CARBON: false,
    CAN_DONATE: false,
    CAN_ACCESS_AI_CURATOR: true,
    MAX_BOOKMARKS: 5,
  },
};

// ============================================================================
// Environment Configuration
// ============================================================================
export const ENV_CONFIG = {
  DEV: import.meta.env.DEV,
  PLATFORM: 'web',
  IS_IOS: isIOS(),
  IS_ANDROID: isAndroid(),
  IS_MOBILE: isMobile(),
  
  DEBUG: {
    API_LOGS: import.meta.env.DEV,
    REDUX_LOGS: false,
    CONSOLE_LOGS: import.meta.env.DEV,
    ENABLE_HEALTH_MONITORING: false,
  },
  
  SERVICES: {
    ANALYTICS_ID: import.meta.env.DEV ? 'dev-analytics-id' : 'prod-analytics-id',
    CRASH_REPORTING: !import.meta.env.DEV,
    PERFORMANCE_MONITORING: !import.meta.env.DEV,
  },
  
  NETWORK: {
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,
  },
};

// ============================================================================
// 🔥 유틸리티 함수들
// ============================================================================
export const buildApiUrl = (endpoint, params = {}) => {
  let url = `${API_CONFIG.FULL_URL}${endpoint}`;
  
  Object.keys(params).forEach(key => {
    url = url.replace(`:${key}`, params[key]);
  });
  
  return url;
};

export const canGuestAccess = (feature) => {
  const restrictions = APP_CONFIG.GUEST_RESTRICTIONS;
  const featureMap = {
    'companies': restrictions.CAN_VIEW_COMPANIES,
    'events': restrictions.CAN_VIEW_EVENTS,
    'meetings': restrictions.CAN_REQUEST_MEETING,
    'chat': restrictions.CAN_USE_CHAT,
    'carbon': restrictions.CAN_EARN_CARBON,
    'donations': restrictions.CAN_DONATE,
    'ai_curator': restrictions.CAN_ACCESS_AI_CURATOR,
  };
  
  return featureMap[feature] !== undefined ? featureMap[feature] : false;
};

export const calculateGuestExpiration = (createdAt) => {
  const created = new Date(createdAt);
  const expirationDays = APP_CONFIG.LIMITS.GUEST_SESSION_DAYS;
  const expiration = new Date(created.getTime() + (expirationDays * 24 * 60 * 60 * 1000));
  
  return {
    expiresAt: expiration,
    daysRemaining: Math.ceil((expiration - new Date()) / (24 * 60 * 60 * 1000)),
    isExpired: new Date() > expiration,
  };
};

export const isValidAuthResponse = (response) => {
  if (!response) {
    logger.error('❌ Response is null or undefined');
    return false;
  }
  
  if (!response.access_token || typeof response.access_token !== 'string') {
    logger.error('❌ Missing or invalid access_token');
    return false;
  }
  
  if (!response.user || typeof response.user !== 'object') {
    logger.error('❌ Missing or invalid user object');
    return false;
  }
  
  logger.debug('✅ Valid auth response:', {
    hasToken: !!response.access_token,
    hasUser: !!response.user,
    userId: response.user?.id,
    userEmail: response.user?.email,
  });
  
  return true;
};

export const isValidRegisterResponse = (response) => {
  if (!isValidAuthResponse(response)) {
    return false;
  }
  
  const user = response.user;
  if (user.user_type === 'COMPANY') {
    if (user.company_created === false && user.company_error) {
      logger.warn('⚠️ 회원가입 성공, 기업 정보 생성 실패:', user.company_error);
    } else if (user.company_created === true) {
      logger.debug('✅ 회원가입 및 기업 정보 생성 성공:', user.company_id);
    }
  }
  
  return true;
};

export const validateConfig = () => {
  const issues = [];
  
  if (!API_CONFIG.BASE_URL) {
    issues.push('API_CONFIG.BASE_URL이 설정되지 않음');
  }
  
  if (OAUTH_CONFIG.ENABLED && !OAUTH_CONFIG.isConfigured?.()) {
    issues.push('OAuth가 활성화되었지만 올바르게 설정되지 않음');
  }
  
  return {
    isValid: issues.length === 0,
    issues,
  };
};

export const logConfigInfo = () => {
  if (import.meta.env.DEV) {
    logger.debug('='.repeat(80));
    logger.debug('📋 App Configuration:');
    logger.debug('='.repeat(80));
    logger.debug('  Name:', APP_CONFIG.NAME);
    logger.debug('  Version:', APP_CONFIG.VERSION);
    logger.debug('  Environment:', ENV_CONFIG.DEV ? 'development' : 'production');
    logger.debug('  Platform:', ENV_CONFIG.PLATFORM);
    logger.debug('  API URL:', API_CONFIG.FULL_URL);
    logger.debug('  BASE URL:', API_CONFIG.BASE_URL);
    logger.debug('  OAuth Enabled:', OAUTH_CONFIG.ENABLED);
    logger.debug('  Guest Login:', APP_CONFIG.FEATURES.GUEST_LOGIN);
    logger.debug('='.repeat(80));
    
    const validation = validateConfig();
    if (!validation.isValid) {
      logger.warn('⚠️ Configuration issues:', validation.issues);
    } else {
      logger.debug('✅ Configuration validated successfully');
    }
    logger.debug('='.repeat(80));
  }
};

// ============================================================================
// Export default
// ============================================================================
export default {
  API_CONFIG,
  OAUTH_CONFIG,
  APP_CONFIG,
  ENV_CONFIG,
};
